import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import theme from '../theme.config';
const BrandLogo = theme.assets.logo;

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password) {
      toast.error('Semua field harus diisi');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Password tidak cocok');
      return;
    }

    if (password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    setLoading(true);
    try {
      const user = await register(name, email, password);
      toast.success(`Selamat datang, ${user.name}!`);
      navigate('/');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Registrasi gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-4 py-8 cyber-grid">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={BrandLogo} alt={theme.brand.name} className="h-14 w-auto object-contain" />
          </div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
            <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">NEW_IDENTITY · REGISTER</span>
          </div>
          <h1 className="font-bold text-2xl text-white uppercase tracking-wider">Buat Akun Baru</h1>
          <p className="text-muted-foreground text-sm mt-2">Daftarkan identitas untuk bergabung dengan aliansi AstraFlux</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-secondary/50 rounded-tl-xl" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary/50 rounded-br-xl" />

          <div className="space-y-4">
            {[
              { id: 'name', label: 'Nama Commander', type: 'text', placeholder: 'Masukkan nama', value: name, setter: setName, testId: 'register-name' },
              { id: 'email', label: 'Email', type: 'email', placeholder: 'commander@astraflux.io', value: email, setter: setEmail, testId: 'register-email' },
            ].map(f => (
              <div key={f.id} className="space-y-2">
                <label htmlFor={f.id} className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ {f.label}</label>
                <Input id={f.id} type={f.type} placeholder={f.placeholder}
                  className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                  value={f.value} onChange={(e) => f.setter(e.target.value)} data-testid={f.testId} />
              </div>
            ))}

            <div className="space-y-2">
              <label htmlFor="password" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ Password</label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Min. 6 karakter"
                  className="bg-black/50 border-border text-white pr-10 font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                  value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                  onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ Konfirmasi Password</label>
              <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Ulangi password"
                className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="register-confirm-password" />
            </div>

            <Button type="submit"
              className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green"
              disabled={loading} data-testid="register-submit">
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                  Initializing...
                </span>
              ) : <span className="font-mono">[ DAFTAR SEKARANG ]</span>}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground text-sm font-mono">
              Sudah terdaftar?{' '}
              <Link to="/login" className="text-primary hover:text-primary/80 underline underline-offset-2">Masuk di sini</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
